import type { store as appStore } from '../createStore';
import { setApiConnectionStatusHandler } from './apiClient';
import { subscribeStorageChanges } from './cloudsave/storageMutations';
import { cloudsaveQueryApi } from './cloudsave/cloudsaveQueryApi';
import { getCurrentUserId, subscribeUserIdentity } from '../auth/userIdentity';
import { userIdentityChanged, sessionRecovered } from '../auth/identityActions';
import { setOnline } from '../store/connection/actions';
import { subscribeSessionRecovery } from '../auth/tokenRecovery';
import { requestConnectivityCheck } from '../util/connectivityChecks';

export function connectApiState(
  store: Pick<typeof appStore, 'dispatch' | 'getState' | 'subscribe'>,
) {
  const invalidateProjects = () => {
    if (store.getState().connectionState.isOnline) {
      store.dispatch(cloudsaveQueryApi.util.invalidateTags(['StorageLists']));
    }
  };
  // A response proves connectivity; a failure from one service only asks the
  // monitor to verify, since other backends may still be reachable.
  const disconnectTransport = setApiConnectionStatusHandler((online) => {
    if (!online) {
      requestConnectivityCheck();
    } else if (!store.getState().connectionState.isOnline) {
      store.dispatch(setOnline());
    }
  });
  const disconnectStorage = subscribeStorageChanges(invalidateProjects);
  let wasOnline = store.getState().connectionState.isOnline;
  const disconnectConnection = store.subscribe(() => {
    const online = store.getState().connectionState.isOnline;
    const reconnected = online && !wasOnline;
    wasOnline = online;
    if (reconnected) invalidateProjects();
  });
  const disconnectIdentity = subscribeUserIdentity(() => {
    store.dispatch(userIdentityChanged(getCurrentUserId()));
    store.dispatch(cloudsaveQueryApi.util.resetApiState());
  });
  const disconnectRecovery = subscribeSessionRecovery(() => {
    store.dispatch(sessionRecovered());
    invalidateProjects();
  });
  return () => {
    disconnectRecovery();
    disconnectIdentity();
    disconnectConnection();
    disconnectStorage();
    disconnectTransport();
  };
}
